// Shared add-new picker modals (TPA-MATTER-CREATION-1, 2026-07-17).
// Used by the new-claim / new-TPA-matter modal AND the claim General-tab edit
// form so every vessel picker (and the Insured picker) gets a consistent
// "+ Add new" affordance. Vessels are reference data; a new Insured is a company.
import { useState, useRef } from "react";
import { apiFetch } from "../api";

// ONE consistent picker mechanism for every entity dropdown (Kuhrt 2026-07-17):
// a searchable input + result list + an optional pinned "+ Add new" row. Works
// two ways: pass `options` (static list, filtered client-side) OR `fetcher`
// (async server search). When a value is selected it collapses to a chip with a
// Change button. Reused for Insured, Vessel, and the IA-file link picker.
export function Combo({ placeholder, options, fetcher, value, display, onPick, onClear, onAddNew, addLabel = "+ Add new" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const pad = { padding: 8, fontSize: 12, color: "var(--text3)" };
  const row = { padding: "7px 9px", fontSize: 13, cursor: "pointer", borderBottom: "1px solid var(--border)" };

  const runFetch = (text) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await fetcher(text.trim()); setResults(Array.isArray(r) ? r : []); }
      catch { setResults([]); }
      setLoading(false);
    }, 250);
  };
  const onType = (text) => {
    setQ(text); setOpen(true);
    if (fetcher) { if (text.trim().length >= 2) runFetch(text); else setResults([]); }
  };
  const shown = fetcher
    ? results
    : (options || []).filter(o => !q || (o.label || "").toLowerCase().includes(q.toLowerCase())).slice(0, 60);

  if (value != null && value !== "" && display) {
    return (<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{display}</span>
      <button type="button" className="btn btn-outline" style={{ padding: "3px 10px" }} onClick={onClear}>Change</button>
    </div>);
  }
  return (<div style={{ position: "relative" }}>
    <input value={q} placeholder={placeholder}
      onChange={e => onType(e.target.value)} onFocus={() => setOpen(true)}
      onBlur={() => setTimeout(() => setOpen(false), 150)} />
    {open && (
      <div style={{ position: "absolute", zIndex: 20, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, width: "100%", maxHeight: 230, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,.12)" }}>
        {loading ? <div style={pad}>Searching…</div>
          : shown.length === 0 ? <div style={pad}>{fetcher && q.trim().length < 2 ? "Type at least 2 letters…" : "No matches"}</div>
          : shown.map(o => <div key={o.id} onMouseDown={() => { onPick(o); setOpen(false); setQ(""); }} style={row}>
              {o.label}{o.sub ? <span style={{ color: "var(--text3)" }}> — {o.sub}</span> : null}</div>)}
        {onAddNew && <div onMouseDown={() => { onAddNew(q); setOpen(false); setQ(""); }}
          style={{ ...row, borderBottom: "none", color: "var(--blue, #2563eb)", fontWeight: 600 }}>{addLabel}</div>}
      </div>
    )}
  </div>);
}

// Rendered above the parent modal (higher z-index) so add-new works inline.
function Shell({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header"><h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">{footer}</div>
      </div>
    </div>
  );
}

export function AddVesselModal({ initialName = "", onClose, onCreated }) {
  const [f, setF] = useState({ vessel_name: initialName, official_number: "", vessel_type: "", flag: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.vessel_name.trim()) { setErr("Vessel name is required."); return; }
    setSaving(true); setErr("");
    try { onCreated(await apiFetch("/vessels", { method: "POST", body: f })); }
    catch (e) { setErr(e.message); setSaving(false); }
  };
  return (
    <Shell title="Add Vessel" onClose={onClose} footer={<>
      <button className="btn btn-outline" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? "Adding…" : "Add Vessel"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid">
        <div className="form-group full"><label>Vessel Name *</label>
          <input autoFocus value={f.vessel_name} onChange={e => setF({ ...f, vessel_name: e.target.value })} /></div>
        <div className="form-group"><label>Official / Doc #</label>
          <input value={f.official_number} onChange={e => setF({ ...f, official_number: e.target.value })} /></div>
        <div className="form-group"><label>Type</label>
          <input value={f.vessel_type} onChange={e => setF({ ...f, vessel_type: e.target.value })} placeholder="Tug, Barge, …" /></div>
        <div className="form-group"><label>Flag</label>
          <input value={f.flag} onChange={e => setF({ ...f, flag: e.target.value })} /></div>
      </div>
    </Shell>
  );
}

export function AddCompanyModal({ initialName = "", onClose, onCreated }) {
  const [f, setF] = useState({ company_name: initialName, company_type: "", city: "", state: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.company_name.trim()) { setErr("Company name is required."); return; }
    setSaving(true); setErr("");
    try { onCreated(await apiFetch("/companies", { method: "POST", body: f })); }
    catch (e) { setErr(e.message); setSaving(false); }
  };
  return (
    <Shell title="Add Insured Company" onClose={onClose} footer={<>
      <button className="btn btn-outline" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? "Adding…" : "Add Company"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      <div className="form-grid">
        <div className="form-group full"><label>Company Name *</label>
          <input autoFocus value={f.company_name} onChange={e => setF({ ...f, company_name: e.target.value })} /></div>
        <div className="form-group"><label>City</label>
          <input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} /></div>
        <div className="form-group"><label>State</label>
          <input value={f.state} onChange={e => setF({ ...f, state: e.target.value })} /></div>
      </div>
      <p style={{ fontSize: 12, color: "var(--text3)", margin: "8px 0 0" }}>
        New companies are flagged for admin review; you can still select this one now.
      </p>
    </Shell>
  );
}
